import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BookOpenIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileTextIcon,
  GripVerticalIcon,
  HelpCircleIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  SettingsIcon,
  Trash2Icon,
} from 'lucide-react';
import type { Course, CourseAudience, CourseStatus, Lesson, Question } from '@kennisbank/shared';
import { ApiError } from '../../api/client.js';
import {
  type CourseDetail,
  type ModuleWithChildren,
  getCourse,
  createCourse,
  updateCourse,
  createModule,
  updateModule,
  deleteModule,
  reorderModules,
  reorderLessons,
  reorderQuestions,
} from './AdminElearningApi.js';
import { AdminLessonModal } from './AdminLessonModal.js';
import { AdminQuestionModal } from './AdminQuestionModal.js';

const inputCls =
  'w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none';

/**
 * Cursuseditor — geport van MoveLogic `CourseEditorPage` (1454 regels),
 * met de `@platform/ui`-componenten vervangen door kennisbank-styling en de
 * Supabase-hooks door REST-calls. Twee tabs: Instellingen (cursus-metadata +
 * exameninstellingen) en Inhoud (modules met dnd-kit drag-drop; per moduul
 * lessen + vragen, elk met eigen drag-drop en modals).
 *
 * Weggelaten t.o.v. MoveLogic (buiten offerte-scope): prijs/aanbieder,
 * "bijwerken cursisten"-push (geen course snapshots in dit datamodel),
 * chauffeur-zichtbaarheid.
 */
export function AdminCourseEditor({
  courseId: initialCourseId,
  onBack,
  onCourseCreated,
  onOpenDashboard,
}: {
  courseId: string | null;
  onBack: () => void;
  onCourseCreated: (id: string) => void;
  onOpenDashboard: (id: string) => void;
}): JSX.Element {
  const [courseId, setCourseId] = useState<string | null>(initialCourseId);
  const isNew = courseId === null;

  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState<'inhoud' | 'instellingen'>(
    isNew ? 'instellingen' : 'inhoud',
  );

  // Settings-form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructor, setInstructor] = useState('');
  const [certificateEnabled, setCertificateEnabled] = useState(true);
  const [status, setStatus] = useState<CourseStatus>('draft');
  const [audience, setAudience] = useState<CourseAudience>('iedereen');
  const [passPercentage, setPassPercentage] = useState(60);
  const [maxRetakes, setMaxRetakes] = useState(3);
  const [cooldownMinutes, setCooldownMinutes] = useState(15);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addingModule, setAddingModule] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyCourseToForm = useCallback((c: Course) => {
    setName(c.name);
    setDescription(c.description ?? '');
    setInstructor(c.instructor ?? '');
    setCertificateEnabled(c.certificateEnabled !== false);
    setStatus(c.status);
    setAudience(c.audience);
    setPassPercentage(c.passPercentage);
    setMaxRetakes(c.maxRetakes);
    setCooldownMinutes(c.cooldownMinutes);
  }, []);

  const reload = useCallback(async () => {
    if (!courseId) return;
    try {
      const d = await getCourse(courseId);
      setDetail(d);
      applyCourseToForm(d.course);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Laden mislukt.');
    } finally {
      setLoading(false);
    }
  }, [courseId, applyCourseToForm]);

  useEffect(() => {
    if (courseId) void reload();
  }, [courseId, reload]);

  async function handleSaveCourse(): Promise<void> {
    setSaving(true);
    setError(null);
    const body = {
      name,
      description,
      instructor,
      certificateEnabled,
      status,
      audience,
      passPercentage,
      maxRetakes,
      cooldownMinutes,
    };
    try {
      if (isNew) {
        const created = await createCourse(body);
        setCourseId(created.id);
        onCourseCreated(created.id);
        setActiveTab('inhoud');
      } else {
        await updateCourse(courseId, body);
        await reload();
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddModule(): Promise<void> {
    if (!courseId) return;
    setAddingModule(true);
    try {
      const nextOrder = (detail?.modules.length ?? 0) + 1;
      await createModule(courseId, { name: `Module ${nextOrder}` });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Toevoegen mislukt.');
    } finally {
      setAddingModule(false);
    }
  }

  async function handleModulesDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;
    if (!over || active.id === over.id || !detail || !courseId) return;
    const ids = detail.modules.map((m) => m.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(detail.modules, oldIndex, newIndex);
    setDetail({ ...detail, modules: reordered });
    try {
      await reorderModules(
        courseId,
        reordered.map((m) => m.id),
      );
    } catch {
      await reload();
    }
  }

  function toggleExpand(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2Icon className="h-8 w-8 animate-spin text-ink-700/40" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Terug naar cursusoverzicht"
            className="shrink-0 rounded-md p-1.5 text-ink-700 hover:bg-brand-50"
          >
            <ChevronLeftIcon size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">
              {isNew ? 'Nieuwe cursus' : 'Cursus bewerken'}
            </h1>
            {!isNew && name && <p className="mt-0.5 text-sm text-ink-700/70">{name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {courseId && (
            <button
              type="button"
              onClick={() => onOpenDashboard(courseId)}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-4 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
            >
              <BookOpenIcon size={14} /> Voortgang
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleSaveCourse()}
            disabled={saving || !name.trim()}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {saving ? (
              <Loader2Icon size={14} className="animate-spin" />
            ) : saved ? (
              <CheckIcon size={14} />
            ) : (
              <SaveIcon size={14} />
            )}
            {saved ? 'Opgeslagen' : 'Opslaan'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex items-end gap-1 border-b border-ring2-border">
        <TabButton
          active={activeTab === 'inhoud'}
          onClick={() => setActiveTab('inhoud')}
          disabled={isNew}
          icon={<BookOpenIcon size={15} />}
          label="Inhoud"
        />
        <TabButton
          active={activeTab === 'instellingen'}
          onClick={() => setActiveTab('instellingen')}
          icon={<SettingsIcon size={15} />}
          label="Instellingen"
        />
      </div>

      {activeTab === 'instellingen' ? (
        <SettingsTab
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          instructor={instructor}
          setInstructor={setInstructor}
          certificateEnabled={certificateEnabled}
          setCertificateEnabled={setCertificateEnabled}
          status={status}
          setStatus={setStatus}
          audience={audience}
          setAudience={setAudience}
          passPercentage={passPercentage}
          setPassPercentage={setPassPercentage}
          maxRetakes={maxRetakes}
          setMaxRetakes={setMaxRetakes}
          cooldownMinutes={cooldownMinutes}
          setCooldownMinutes={setCooldownMinutes}
        />
      ) : !courseId ? (
        <EmptyFirstSave onGoSettings={() => setActiveTab('instellingen')} />
      ) : (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold text-ink-900">Modules</h2>
            <button
              type="button"
              onClick={() => void handleAddModule()}
              disabled={addingModule}
              className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
            >
              {addingModule ? (
                <Loader2Icon size={14} className="animate-spin" />
              ) : (
                <PlusIcon size={14} />
              )}
              Module toevoegen
            </button>
          </div>

          {detail && detail.modules.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => void handleModulesDragEnd(e)}
            >
              <SortableContext
                items={detail.modules.map((m) => m.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {detail.modules.map((m) => (
                    <SortableModule
                      key={m.id}
                      module={m}
                      courseId={courseId}
                      expanded={expanded.has(m.id)}
                      onToggle={() => toggleExpand(m.id)}
                      sensors={sensors}
                      onChanged={reload}
                      onError={setError}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="rounded-xl border border-dashed border-brand-200 bg-white py-12 text-center">
              <BookOpenIcon className="mx-auto mb-2 h-8 w-8 text-ink-700/30" />
              <p className="text-sm text-ink-700/70">
                Voeg modules toe om lessen en vragen te organiseren.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ─── Tab button ──────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  disabled,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: JSX.Element;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`relative -mb-px inline-flex items-center gap-2 rounded-t-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? 'border-b-2 border-brand-700 bg-white text-brand-800'
          : 'text-ink-700/70 hover:bg-brand-50/40 hover:text-ink-900'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Settings tab ────────────────────────────────────────────────────────────

function SettingsTab(props: {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  instructor: string;
  setInstructor: (v: string) => void;
  certificateEnabled: boolean;
  setCertificateEnabled: (v: boolean) => void;
  status: CourseStatus;
  setStatus: (v: CourseStatus) => void;
  audience: CourseAudience;
  setAudience: (v: CourseAudience) => void;
  passPercentage: number;
  setPassPercentage: (v: number) => void;
  maxRetakes: number;
  setMaxRetakes: (v: number) => void;
  cooldownMinutes: number;
  setCooldownMinutes: (v: number) => void;
}): JSX.Element {
  const statuses: { value: CourseStatus; label: string }[] = [
    { value: 'draft', label: 'Concept' },
    { value: 'active', label: 'Actief' },
    { value: 'archived', label: 'Archief' },
  ];
  const audiences: { value: CourseAudience; label: string; hint: string }[] = [
    {
      value: 'iedereen',
      label: 'Iedereen',
      hint: 'Alle ingelogde gebruikers, ook externe behandelaren',
    },
    {
      value: 'intern',
      label: 'SUPPOHRT Intern',
      hint: 'Alleen SUPPOHRT-medewerkers en beheerders',
    },
    { value: 'hoofdkantoor', label: 'SUPPOHRT Kantoor', hint: 'Alleen de Kantoorpersoneel-groep' },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-brand-100 bg-white p-6 shadow-card">
        <h3 className="mb-4 text-base font-bold text-ink-900">Cursusgegevens</h3>
        <div className="space-y-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Naam *
            </label>
            <input
              type="text"
              value={props.name}
              onChange={(e) => props.setName(e.target.value)}
              placeholder="Naam van de cursus"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Beschrijving
            </label>
            <textarea
              value={props.description}
              onChange={(e) => props.setDescription(e.target.value)}
              rows={3}
              placeholder="Korte beschrijving van de cursus…"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Docent (optioneel)
            </label>
            <input
              type="text"
              value={props.instructor}
              onChange={(e) => props.setInstructor(e.target.value)}
              placeholder="Naam van de docent of spreker"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-ink-700/60">
              Komt op het certificaat als &quot;onder leiding van&quot;.
            </p>
          </div>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={props.certificateEnabled}
              onChange={(e) => props.setCertificateEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-ink-100 text-brand-600 focus:ring-brand-500"
            />
            <span>
              <span className="block text-sm font-medium text-ink-900">
                Certificaat na afronden
              </span>
              <span className="block text-xs text-ink-700/60">
                Uit: de cursist ziet na afronden alleen een felicitatie, zonder downloadknop.
              </span>
            </span>
          </label>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Doelgroep
            </label>
            <div className="space-y-2">
              {audiences.map((a) => (
                <label
                  key={a.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-brand-100 p-3 hover:bg-brand-50/50"
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={props.audience === a.value}
                    onChange={() => props.setAudience(a.value)}
                    className="mt-0.5 h-4 w-4 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink-900">{a.label}</span>
                    <span className="block text-xs text-ink-700/60">{a.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Status
            </label>
            <div className="flex gap-2">
              {statuses.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => props.setStatus(s.value)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    props.status === s.value
                      ? 'bg-brand-700 text-white'
                      : 'border border-brand-200 bg-white text-ink-800 hover:bg-brand-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-brand-100 bg-white p-6 shadow-card">
        <h3 className="mb-1 text-base font-bold text-ink-900">Exameninstellingen</h3>
        <p className="mb-4 text-xs text-ink-700/60">
          Gelden voor het hele examen. Hoeveel vragen elke module aan het examen levert, stel je per
          module in.
        </p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <NumberField
            label="Slagingspercentage"
            suffix="%"
            min={0}
            max={100}
            value={props.passPercentage}
            onChange={props.setPassPercentage}
          />
          <NumberField
            label="Maximaal aantal herkansingen"
            min={0}
            max={50}
            value={props.maxRetakes}
            onChange={props.setMaxRetakes}
          />
          <NumberField
            label="Wachttijd tussen pogingen"
            suffix="min"
            min={0}
            max={10080}
            value={props.cooldownMinutes}
            onChange={props.setCooldownMinutes}
          />
        </div>
      </div>
    </div>
  );
}

function NumberField({
  label,
  suffix,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  suffix?: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange(Number.isNaN(n) ? min : Math.min(max, Math.max(min, n)));
          }}
          className={inputCls}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-700/50">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyFirstSave({ onGoSettings }: { onGoSettings: () => void }): JSX.Element {
  return (
    <div className="rounded-xl border border-brand-100 bg-white py-16 text-center shadow-card">
      <SettingsIcon className="mx-auto mb-4 h-10 w-10 text-ink-700/30" />
      <h3 className="mb-1 text-lg font-semibold text-ink-900">Eerst opslaan</h3>
      <p className="mx-auto max-w-sm text-sm text-ink-700/70">
        Vul de cursusgegevens in op het tabblad Instellingen en sla op om modules toe te voegen.
      </p>
      <button
        type="button"
        onClick={onGoSettings}
        className="mt-4 rounded-full border border-brand-200 bg-white px-4 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
      >
        Naar instellingen
      </button>
    </div>
  );
}

// ─── Sortable module ─────────────────────────────────────────────────────────

function SortableModule({
  module,
  courseId,
  expanded,
  onToggle,
  sensors,
  onChanged,
  onError,
}: {
  module: ModuleWithChildren;
  courseId: string;
  expanded: boolean;
  onToggle: () => void;
  sensors: ReturnType<typeof useSensors>;
  onChanged: () => Promise<void>;
  onError: (msg: string) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: module.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(module.name);

  async function saveName(): Promise<void> {
    const trimmed = editName.trim();
    setEditing(false);
    if (!trimmed || trimmed === module.name) {
      setEditName(module.name);
      return;
    }
    try {
      await updateModule(courseId, module.id, { name: trimmed });
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Hernoemen mislukt.');
    }
  }

  async function handleDelete(): Promise<void> {
    if (
      !window.confirm(
        'Weet je zeker dat je deze module wilt verwijderen? Alle lessen en vragen worden ook verwijderd.',
      )
    )
      return;
    try {
      await deleteModule(courseId, module.id);
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
    }
  }

  async function saveExamCounts(
    field: 'examRequiredCount' | 'examRandomCount',
    value: number,
  ): Promise<void> {
    try {
      await updateModule(courseId, module.id, { [field]: value });
      await onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    }
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`rounded-xl border bg-white shadow-card ${
          isDragging ? 'border-brand-400 ring-2 ring-brand-200' : 'border-brand-100'
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            className="cursor-grab text-ink-700/40 hover:text-ink-700"
            aria-label="Sleep om te herordenen"
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon size={18} />
          </button>

          {editing ? (
            <div className="flex flex-1 items-center gap-2">
              <input
                type="text"
                value={editName}
                autoFocus
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveName();
                  if (e.key === 'Escape') {
                    setEditName(module.name);
                    setEditing(false);
                  }
                }}
                onBlur={() => void saveName()}
                className="flex-1 rounded-md border border-brand-300 px-2 py-1 text-base font-semibold text-ink-900 focus:border-brand-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => void saveName()}
                className="rounded-md p-1 text-brand-700 hover:bg-brand-50"
              >
                <CheckIcon size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onToggle}
              className="group flex flex-1 items-center gap-2 text-left"
            >
              {expanded ? (
                <ChevronDownIcon size={16} className="text-ink-700/50" />
              ) : (
                <ChevronRightIcon size={16} className="text-ink-700/50" />
              )}
              <span className="text-base font-semibold text-ink-900">{module.name}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditName(module.name);
                  setEditing(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    setEditName(module.name);
                    setEditing(true);
                  }
                }}
                title="Naam bewerken"
                className="opacity-0 transition-opacity group-hover:opacity-100"
              >
                <PencilIcon size={13} className="text-ink-700/40 hover:text-ink-700" />
              </span>
            </button>
          )}

          <div className="flex shrink-0 items-center gap-3 text-xs text-ink-700/60">
            <span className="inline-flex items-center gap-1">
              <FileTextIcon size={13} />
              {module.lessons.length} {module.lessons.length === 1 ? 'les' : 'lessen'}
            </span>
            <span className="inline-flex items-center gap-1">
              <HelpCircleIcon size={13} />
              {module.questions.length} {module.questions.length === 1 ? 'vraag' : 'vragen'}
            </span>
          </div>

          <button
            type="button"
            onClick={() => void handleDelete()}
            aria-label="Module verwijderen"
            className="rounded-md p-1.5 text-ink-700/50 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2Icon size={15} />
          </button>
        </div>

        {expanded && (
          <div className="border-t border-brand-50 px-4 py-4">
            <ModuleBody
              module={module}
              courseId={courseId}
              sensors={sensors}
              onChanged={onChanged}
              onSaveExamCount={saveExamCounts}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Module body: exam counts + lessons + questions ──────────────────────────

function ModuleBody({
  module,
  courseId,
  sensors,
  onChanged,
  onSaveExamCount,
}: {
  module: ModuleWithChildren;
  courseId: string;
  sensors: ReturnType<typeof useSensors>;
  onChanged: () => Promise<void>;
  onSaveExamCount: (field: 'examRequiredCount' | 'examRandomCount', value: number) => Promise<void>;
}): JSX.Element {
  const [lessonModal, setLessonModal] = useState<{ lesson: Lesson | null } | null>(null);
  const [questionModal, setQuestionModal] = useState<{ question: Question | null } | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>(module.lessons);
  const [questions, setQuestions] = useState<Question[]>(module.questions);

  // Keep local drag-order in sync when the parent reloads the detail.
  useEffect(() => setLessons(module.lessons), [module.lessons]);
  useEffect(() => setQuestions(module.questions), [module.questions]);

  async function onLessonsDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = lessons.map((l) => l.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(lessons, oldIndex, newIndex);
    setLessons(reordered);
    try {
      await reorderLessons(
        courseId,
        module.id,
        reordered.map((l) => l.id),
      );
    } catch {
      await onChanged();
    }
  }

  async function onQuestionsDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = questions.map((q) => q.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(questions, oldIndex, newIndex);
    setQuestions(reordered);
    try {
      await reorderQuestions(
        courseId,
        module.id,
        reordered.map((q) => q.id),
      );
    } catch {
      await onChanged();
    }
  }

  return (
    <div className="space-y-6">
      {/* Exam counts */}
      <div className="grid grid-cols-2 gap-4 rounded-lg bg-brand-50/40 p-3">
        <ExamCountField
          label="Verplichte examenvragen"
          value={module.examRequiredCount}
          onSave={(v) => onSaveExamCount('examRequiredCount', v)}
        />
        <ExamCountField
          label="Willekeurige examenvragen"
          value={module.examRandomCount}
          onSave={(v) => onSaveExamCount('examRandomCount', v)}
        />
      </div>

      {/* Lessons */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-700/70">Lessen</h4>
          <button
            type="button"
            onClick={() => setLessonModal({ lesson: null })}
            className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
          >
            <PlusIcon size={13} /> Les toevoegen
          </button>
        </div>
        {lessons.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => void onLessonsDragEnd(e)}
          >
            <SortableContext
              items={lessons.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {lessons.map((l) => (
                  <SortableRow
                    key={l.id}
                    id={l.id}
                    icon={<FileTextIcon size={14} className="text-ink-700/50" />}
                    title={l.name}
                    inactive={!l.isLive}
                    onEdit={() => setLessonModal({ lesson: l })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="py-1 text-sm italic text-ink-700/50">Nog geen lessen toegevoegd.</p>
        )}
      </div>

      {/* Questions */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-700/70">Vragen</h4>
          <button
            type="button"
            onClick={() => setQuestionModal({ question: null })}
            className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
          >
            <PlusIcon size={13} /> Vraag toevoegen
          </button>
        </div>
        {questions.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => void onQuestionsDragEnd(e)}
          >
            <SortableContext
              items={questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {questions.map((q) => (
                  <SortableRow
                    key={q.id}
                    id={q.id}
                    icon={<HelpCircleIcon size={14} className="text-ink-700/50" />}
                    title={q.title}
                    inactive={!q.isLive}
                    badge={
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          q.category === 'examen'
                            ? 'border-petrol-100 bg-petrol-50 text-petrol-700'
                            : 'border-mosterdgeel-100 bg-mosterdgeel-10 text-mosterdgeel-600'
                        }`}
                      >
                        {q.category === 'examen' ? 'Examen' : 'Oefening'}
                      </span>
                    }
                    onEdit={() => setQuestionModal({ question: q })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="py-1 text-sm italic text-ink-700/50">Nog geen vragen toegevoegd.</p>
        )}
      </div>

      {lessonModal &&
        createPortal(
          <AdminLessonModal
            courseId={courseId}
            moduleId={module.id}
            lesson={lessonModal.lesson}
            onClose={() => setLessonModal(null)}
            onSaved={() => {
              setLessonModal(null);
              void onChanged();
            }}
          />,
          document.body,
        )}
      {questionModal &&
        createPortal(
          <AdminQuestionModal
            courseId={courseId}
            moduleId={module.id}
            question={questionModal.question}
            onClose={() => setQuestionModal(null)}
            onSaved={() => {
              setQuestionModal(null);
              void onChanged();
            }}
          />,
          document.body,
        )}
    </div>
  );
}

function ExamCountField({
  label,
  value,
  onSave,
}: {
  label: string;
  value: number;
  onSave: (v: number) => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-700/60">
        {label}
      </label>
      <input
        type="number"
        min={0}
        max={200}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const n = Math.min(200, Math.max(0, parseInt(draft, 10) || 0));
          if (n !== value) void onSave(n);
          else setDraft(String(value));
        }}
        className="w-full rounded-md border border-ring2-border bg-white px-2 py-1 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
      />
    </div>
  );
}

// ─── Sortable list row (lesson or question) ──────────────────────────────────

function SortableRow({
  id,
  icon,
  title,
  inactive,
  badge,
  onEdit,
}: {
  id: string;
  icon: JSX.Element;
  title: string;
  inactive: boolean;
  badge?: JSX.Element;
  onEdit: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between rounded-md border bg-brand-50/30 px-3 py-2 ${
        isDragging ? 'border-brand-300' : 'border-brand-100'
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-ink-700/30 hover:text-ink-700"
          aria-label="Sleep om te herordenen"
          {...attributes}
          {...listeners}
        >
          <GripVerticalIcon size={15} />
        </button>
        {icon}
        <span className="truncate text-sm font-medium text-ink-900">{title}</span>
        {badge}
        {inactive && (
          <span className="inline-flex items-center rounded-full border border-ink-100 bg-ink-50 px-2 py-0.5 text-[11px] font-medium text-ink-500/70">
            Inactief
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
      >
        Bewerken
      </button>
    </div>
  );
}
